import { normalizeUsernameForDisplay } from "../utils/username";

export const REGISTRATION_VERIFICATION_TTL_MS = 10 * 60 * 1000;
export const REGISTRATION_RESEND_COOLDOWN_MS = 60 * 1000;

const STORAGE_KEY = "muzone.auth.registrationChallenge.v1";
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const hasWindow = () => typeof window !== "undefined";

const resolveStorage = () => {
    if (!hasWindow()) {
        return null;
    }

    try {
        return window.localStorage;
    } catch (_) {
        return null;
    }
};

const normalizeChallengeId = (value) => {
    if (typeof value !== "string") {
        return null;
    }

    const trimmed = value.trim();
    if (!UUID_PATTERN.test(trimmed)) {
        return null;
    }

    return trimmed.toLowerCase();
};

const normalizeEmail = (value) => {
    if (typeof value !== "string") {
        return null;
    }

    const trimmed = value.trim().toLowerCase();
    return trimmed || null;
};

const normalizeUsername = (value) => {
    return normalizeUsernameForDisplay(value);
};

const normalizePasswordRequired = (value) => value === true;

const readStorage = () => {
    const storage = resolveStorage();
    if (!storage) {
        return null;
    }

    try {
        return storage.getItem(STORAGE_KEY);
    } catch (_) {
        return null;
    }
};

const writeStorage = (value) => {
    const storage = resolveStorage();
    if (!storage) {
        return;
    }

    try {
        storage.setItem(STORAGE_KEY, JSON.stringify(value));
    } catch (_) {
        // Ignore storage failures and keep the flow in-memory only.
    }
};

export const clearPendingRegistrationChallenge = () => {
    const storage = resolveStorage();
    if (!storage) {
        return;
    }

    try {
        storage.removeItem(STORAGE_KEY);
    } catch (_) {
        // Ignore storage failures.
    }
};

export const persistPendingRegistrationChallenge = ({
    challengeId,
    email,
    username,
    passwordRequired = false,
    issuedAt = Date.now(),
    expiresAt,
    resendAvailableAt,
    preserveExistingTiming = false,
}) => {
    const normalizedChallengeId = normalizeChallengeId(challengeId);
    const normalizedEmail = normalizeEmail(email);
    const normalizedUsername = normalizeUsername(username);
    const normalizedPasswordRequired = normalizePasswordRequired(passwordRequired);
    const normalizedIssuedAt = Number(issuedAt);
    const normalizedExpiresAt = Number.isFinite(expiresAt)
        ? Number(expiresAt)
        : normalizedIssuedAt + REGISTRATION_VERIFICATION_TTL_MS;
    const normalizedResendAvailableAt = Number.isFinite(resendAvailableAt)
        ? Number(resendAvailableAt)
        : normalizedIssuedAt + REGISTRATION_RESEND_COOLDOWN_MS;
    const hasExplicitTiming = Number.isFinite(expiresAt) || Number.isFinite(resendAvailableAt);

    if (
        !normalizedChallengeId
        || !normalizedEmail
        || !Number.isFinite(normalizedIssuedAt)
        || !Number.isFinite(normalizedExpiresAt)
        || !Number.isFinite(normalizedResendAvailableAt)
        || normalizedExpiresAt < normalizedIssuedAt
        || normalizedResendAvailableAt < normalizedIssuedAt
    ) {
        clearPendingRegistrationChallenge();
        return null;
    }

    if (preserveExistingTiming && !hasExplicitTiming) {
        const activeChallenge = readPendingRegistrationChallenge(normalizedIssuedAt);
        if (activeChallenge && activeChallenge.challengeId === normalizedChallengeId) {
            const nextValue = {
                ...activeChallenge,
                email: normalizedEmail,
                passwordRequired: normalizedPasswordRequired,
            };
            const nextUsername = normalizedUsername ?? activeChallenge.username;
            if (nextUsername) {
                nextValue.username = nextUsername;
            } else {
                delete nextValue.username;
            }
            writeStorage(nextValue);
            return nextValue;
        }
    }

    const nextValue = {
        challengeId: normalizedChallengeId,
        email: normalizedEmail,
        passwordRequired: normalizedPasswordRequired,
        issuedAt: normalizedIssuedAt,
        expiresAt: normalizedExpiresAt,
        resendAvailableAt: normalizedResendAvailableAt,
    };

    if (normalizedUsername) {
        nextValue.username = normalizedUsername;
    }

    writeStorage(nextValue);
    return nextValue;
};

export const readPendingRegistrationChallenge = (now = Date.now()) => {
    const raw = readStorage();
    if (!raw) {
        return null;
    }

    try {
        const parsed = JSON.parse(raw);
        const challengeId = normalizeChallengeId(parsed?.challengeId);
        const email = normalizeEmail(parsed?.email);
        const username = normalizeUsername(parsed?.username);
        const passwordRequired = normalizePasswordRequired(parsed?.passwordRequired);
        const issuedAt = Number(parsed?.issuedAt);
        const expiresAt = Number(parsed?.expiresAt);
        const resendAvailableAt = Number(parsed?.resendAvailableAt);

        if (
            !challengeId
            || !email
            || !Number.isFinite(issuedAt)
            || !Number.isFinite(expiresAt)
            || !Number.isFinite(resendAvailableAt)
            || resendAvailableAt < issuedAt
            || expiresAt <= now
        ) {
            clearPendingRegistrationChallenge();
            return null;
        }

        return {
            challengeId,
            email,
            username,
            passwordRequired,
            issuedAt,
            expiresAt,
            resendAvailableAt,
        };
    } catch (_) {
        clearPendingRegistrationChallenge();
        return null;
    }
};
